import { Outlet, useLocation } from 'react-router-dom';
import logout from '../../../helpers/auth/logout';
import { useIdleLogout } from '../../../hooks/useCheckActivity';
import { LayoutProvider } from '../../../storage/LayoutContext';
import { DigitalProvider } from '../../../context/digital';

/**
 * RootLayout - A wrapper component that maintains hooks across all route changes
 *
 * This component wraps all routes and ensures that hooks defined here
 * persist their state and effects across navigation. This is useful for:
 * - Activity tracking (idle timeout, user activity monitoring)
 * - Authentication checks
 * - Global event listeners
 * - Other hooks that should not remount on route changes
 *
 * Add your hooks here that need to persist across route changes.
 * Example:
 * - import { useIdleLogout } from 'app/hooks/useCheckActivity';
 * - import { useAuthCheck } from 'app/hooks/useAuthCheck';
 */

// Public routes where idle logout should NOT run
// These routes don't require authentication
const PUBLIC_ROUTES = ['/login', '/recovery', '/register', '/password_recovery', '/new-password'];

const isPublicRoute = (pathname: string): boolean => {
  // Check if pathname matches any public route pattern
  // Using includes() is safe here since all routes follow /:lang/route-name pattern

  return PUBLIC_ROUTES.some(route => pathname.includes(route));
};

const isAuthenticated = (): boolean => {
  return !!sessionStorage.getItem('refreshToken');
};

const RootLayout = () => {
  const location = useLocation();
  const shouldRunIdleLogout = isAuthenticated() && !isPublicRoute(location.pathname);

  // Only enable idle logout for authenticated users on protected routes
  useIdleLogout(() => {
    logout();
  }, shouldRunIdleLogout);

  return (
    <LayoutProvider>
      <DigitalProvider>
        <Outlet />
      </DigitalProvider>
    </LayoutProvider>
  );
};

export default RootLayout;
